import React, { useState } from 'react';
import DragHandleIcon from '../atoms/DragHandleIcon';
import Input from '../atoms/Input';
import Select from '../atoms/Select';
import Toggle from '../atoms/Toggle';
import TrashIcon from '../atoms/TrashIcon';
import { isColorOptionsField } from '../../data/formFieldPresets';

const OPTION_FIELD_TYPES = new Set(['Select', 'Dropdown', 'MultiSelect', 'Checkbox', 'Radio']);

function withCurrentOption(options, current) {
  if (!current || options.includes(current)) return options;
  return [current, ...options];
}

function fieldEditSignature(field) {
  return JSON.stringify({
    fieldLabel: String(field.fieldLabel || '').trim(),
    fieldType: field.fieldType ?? '',
    required: Boolean(field.required),
    fieldAccess: field.fieldAccess ?? '',
    hideFor: [...(field.hideFor || [])].sort(),
    dependsOn: field.dependsOn || '',
    options: field.options || [],
    parentMappings: field.parentMappings || {},
    optionColors: field.optionColors || {},
    order: Number(field.order) || 1,
  });
}

const DEFAULT_OPTION_COLOR = '#9CA3AF';

export default function FormCard({
  field,
  fieldOptions = [],
  fieldTypeOptions = [],
  fieldAccessOptions = [],
  hideForOptions = [],
  isSaving = false,
  isDeleting = false,
  selected = false,
  isDragging = false,
  onSelect,
  onDragStartField,
  onDragEndField,
  onReorder,
  onChange,
  onDelete,
  onSave,
}) {
  const [optionDraft, setOptionDraft] = useState('');
  const currentSignature = fieldEditSignature(field);
  const [savedState, setSavedState] = useState(() => ({
    id: field.id,
    signature: field.isNew ? null : currentSignature,
  }));
  if (savedState.id !== field.id) {
    setSavedState({
      id: field.id,
      signature: field.isNew ? null : currentSignature,
    });
  }
  const lockAll = field.lockMode === 'all';
  const lockMeta = lockAll || field.lockMode === 'options';
  const allowsOptions = OPTION_FIELD_TYPES.has(field.fieldType);
  const lockOptions = lockAll || !allowsOptions;
  const hasLabelAndType = Boolean(String(field.fieldLabel || '').trim() && String(field.fieldType || '').trim());
  const isDirty = savedState.signature !== currentSignature;
  const showSave = !lockAll && isDirty;
  const saveDisabled = Boolean(isSaving) || !hasLabelAndType;
  const update = (patch) => onChange?.({ ...field, ...patch });
  const addOption = () => {
    if (lockOptions) return;
    const option = optionDraft.trim();
    if (!option || field.options?.includes(option)) return;
    const nextColors = isColorOptionsField(field)
      ? { ...(field.optionColors || {}), [option]: field.optionColors?.[option] || DEFAULT_OPTION_COLOR }
      : field.optionColors;
    update({
      options: [...(field.options || []), option],
      ...(isColorOptionsField(field) ? { optionColors: nextColors } : {}),
    });
    setOptionDraft('');
  };
  const removeOption = (option) => {
    if (lockOptions) return;
    const nextColors = { ...(field.optionColors || {}) };
    delete nextColors[option];
    update({
      options: field.options.filter((item) => item !== option),
      ...(isColorOptionsField(field) ? { optionColors: nextColors } : {}),
    });
  };

  return (
    <article
      className={`ca-form-card${selected ? ' ca-form-card--selected' : ''}${isDragging ? ' ca-form-card--dragging' : ''}`}
      onClick={() => onSelect?.(field)}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event) => {
        event.preventDefault();
        const fromId = event.dataTransfer.getData('text/plain');
        onReorder?.(fromId, field.id);
        onDragEndField?.();
      }}
    >
      <div className="ca-form-card__row ca-form-card__row--order">
        <span
          className="ca-form-card__drag"
          draggable
          aria-label="Reorder field"
          onClick={(event) => event.stopPropagation()}
          onDragStart={(event) => {
            event.dataTransfer.setData('text/plain', String(field.id));
            event.dataTransfer.effectAllowed = 'move';
            onDragStartField?.();
          }}
          onDragEnd={() => onDragEndField?.()}
        >
          <DragHandleIcon size={15} color="currentColor" />
        </span>
        <div className="ca-form-card__actions">
          {showSave ? (
            <button
              type="button"
              className="ca-form-card__save"
              aria-label="Save form field"
              disabled={saveDisabled}
              onClick={async (event) => {
                event.stopPropagation();
                if (saveDisabled) return;
                const saved = await onSave?.(field);
                if (saved) setSavedState({ id: field.id, signature: fieldEditSignature(saved) });
              }}
            >
              Save
            </button>
          ) : null}
          <button type="button" className="ca-icon-btn ca-form-card__delete" aria-label="Delete form field" disabled={Boolean(isDeleting)} onClick={(event) => {
            event.stopPropagation();
            onDelete?.(field.id);
          }}>
            <TrashIcon size={15} color="currentColor" />
          </button>
        </div>
      </div>
      <div className="ca-form-card__row">
        <Input value={field.fieldLabel} placeholder="Enter field label" disabled={lockMeta} onChange={(event) => update({ fieldLabel: event.target.value })} />
      </div>
      <div className="ca-form-card__row">
        <Select value={field.fieldType} options={withCurrentOption(fieldTypeOptions, field.fieldType)} placeholder="Select type" disabled={lockMeta} onChange={(event) => update({ fieldType: event.target.value })} />
      </div>
      <div className="ca-form-card__row ca-form-card__row--toggle">
        <Toggle checked={Boolean(field.required)} disabled={lockMeta} onChange={(required) => update({ required })} aria-label="Required" />
      </div>
      <div className="ca-form-card__row">
        <Select value={field.fieldAccess} options={withCurrentOption(fieldAccessOptions, field.fieldAccess)} placeholder="Select access" disabled={lockMeta} onChange={(event) => update({ fieldAccess: event.target.value })} />
      </div>
      <div className="ca-form-card__row">
        <Select value="" options={hideForOptions.filter((item) => !field.hideFor?.includes(item))} placeholder="Select level" disabled={lockMeta} onChange={(event) => update({ hideFor: [...(field.hideFor || []), event.target.value] })} />
        {field.hideFor?.length ? <div className="ca-form-card__chips">{field.hideFor.map((item) => <span key={item} className="ca-form-card__chip">{item}<button type="button" aria-label={`Remove ${item}`} disabled={lockMeta} onClick={() => update({ hideFor: field.hideFor.filter((value) => value !== item) })}>×</button></span>)}</div> : null}
      </div>
      <div className="ca-form-card__row">
        <Select value={field.dependsOn} options={withCurrentOption(fieldOptions, field.dependsOn)} placeholder="Select field label" disabled={lockMeta} onChange={(event) => update({ dependsOn: event.target.value })} />
      </div>
      <div className="ca-form-card__row ca-form-card__row--options">
        <div className="ca-form-card__option-entry">
          <Input value={optionDraft} placeholder="Enter option" disabled={lockOptions} onChange={(event) => setOptionDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addOption(); } }} />
          <button type="button" className="ca-form-card__add-option" aria-label="Add option" disabled={lockOptions} onClick={addOption}>+</button>
        </div>
        <div className="ca-form-card__chips">
          {(field.options || []).map((option) => <span className="ca-form-card__chip" key={option}>{option}<button type="button" aria-label={`Remove ${option}`} disabled={lockOptions} onClick={() => removeOption(option)}>×</button></span>)}
        </div>
      </div>
    </article>
  );
}
